import React from 'react';
import { View, TouchableOpacity, Text, Image, StyleSheet, Platform } from 'react-native';
import { useChat } from '../contexts/ChatContext';
import { COLORS } from '../theme/themeTokens';

type TabScreen = 'dashboard' | 'products' | 'history' | 'reports' | 'support';

interface TabBarProps {
    activeTab: TabScreen | null;
    // Legacy screens still declare local route unions. Keep this boundary
    // permissive until navigation is centralised in one shared route type.
    onNavigate: (screen: any) => void;
}

const TabBar: React.FC<TabBarProps> = ({ activeTab, onNavigate }) => {
    const { supportUnreadCount } = useChat();
    const isActive = (tab: TabScreen) => activeTab === tab;

    const handleNavigate = (screen: TabScreen) => {
        onNavigate(screen);
    };

    return (
        <View style={styles.container}>
            {/* iOS skirt: paints the iOS home-indicator gutter white so
                the pureLight iosSplitBottom in App.tsx no longer shows
                through as a gray strip below the white TabBar. Negative
                `bottom` overshoots the screen's safe-area edge; RN's
                default `overflow: visible` lets it render past the
                container's bounds. Android renders its own nav bar bg
                and doesn't have this gutter. */}
            {Platform.OS !== 'android' && <View style={styles.skirt} pointerEvents="none" />}
            {/* Web: iOS Safari tints the area under its toolbar with a fixed element at the bottom edge (the page
                background otherwise — the header's navy). A white strip hidden under the bar. */}
            {Platform.OS === 'web' && <View style={styles.webBottomEdge} pointerEvents="none" />}
            {/* Footer stub to hide content below tab bar */}
            {/* <Image
                source={require('../assets/footer-stub.png')}
                style={styles.footerStub}
                resizeMode="stretch"
            /> */}
            <View style={styles.tabBar}>
                <TouchableOpacity style={styles.tabItem} onPress={() => handleNavigate('dashboard')}>
                    <Image
                        source={
                            isActive('dashboard')
                                ? require('../assets/logo_active.png')
                                : require('../assets/logo_inactive.png')
                        }
                        style={[
                            styles.tabIcon,
                            isActive('dashboard') && { tintColor: undefined },
                        ]}
                        resizeMode="contain"
                    />
                    <Text style={[styles.tabText, isActive('dashboard') && styles.tabTextActive]}>
                        Home
                    </Text>
                </TouchableOpacity>

                {/* <TouchableOpacity style={styles.tabItem} onPress={() => handleNavigate('products')}>
                    <Image
                        source={require('../assets/footer-finance.png')}
                        style={[styles.tabIcon, isActive('products') && styles.tabIconActive]}
                        resizeMode="contain"
                    />
                    <Text style={isActive('products') ? styles.tabTextActive : styles.tabText}>
                        Products
                    </Text>
                </TouchableOpacity> */}

                <TouchableOpacity style={styles.tabItem} onPress={() => handleNavigate('history')}>
                    <Image
                        source={require('../assets/clock-navy.png')}
                        style={[styles.tabIcon, isActive('history') && styles.tabIconActive]}
                        resizeMode="contain"
                    />
                    <Text style={[styles.tabText, isActive('history') && styles.tabTextActive]}>
                        History
                    </Text>
                </TouchableOpacity>

                <TouchableOpacity style={styles.tabItem} onPress={() => handleNavigate('products')}>
                    <Image
                        source={
                            isActive('products')
                                ? require('../assets/coins_active.png')
                                : require('../assets/coins.png')
                        }
                        style={[styles.tabIcon, isActive('products') && styles.tabIconActive]}
                        resizeMode="contain"
                    />
                    <Text style={[styles.tabText, isActive('products') && styles.tabTextActive]}>
                        Products
                    </Text>
                </TouchableOpacity>

                <TouchableOpacity style={styles.tabItem} onPress={() => handleNavigate('support')}>
                    <View style={styles.iconContainer}>
                        <Image
                            source={require('../assets/support.png')}
                            style={[styles.tabIcon, isActive('support') && styles.tabIconActive]}
                            resizeMode="contain"
                        />
                        {supportUnreadCount > 0 && (
                            <View style={styles.badge}>
                                <Text style={styles.badgeText}>
                                    {supportUnreadCount > 99 ? '99+' : supportUnreadCount}
                                </Text>
                            </View>
                        )}
                    </View>
                    <Text style={[styles.tabText, isActive('support') && styles.tabTextActive]}>
                        Support
                    </Text>
                </TouchableOpacity>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
    },
    /* Paints the iOS home-indicator gutter white, hiding the pureLight
       iosSplitBottom that otherwise shows between TabBar and the screen
       edge. Tall enough to cover any iPhone (notch + home indicator). */
    skirt: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: -100,
        height: 100,
        backgroundColor: COLORS.white,
    },
    webBottomEdge: {
        position: 'fixed' as 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height: 20,
        backgroundColor: COLORS.white,
    },
    footerStub: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: Platform.OS === 'ios' ? 84 : 50,
        width: '100%',
    },
    tabBar: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        flexDirection: 'row',
        justifyContent: 'space-around',
        alignItems: 'center',
        backgroundColor: COLORS.white,
        paddingHorizontal: 10,
        // The web is laid out like iOS: the page's SafeAreaView already keeps
        // the bar above the home indicator, so no inset of its own.
        paddingTop: Platform.OS !== 'android' ? 10 : 8,
        // Android draws the gesture indicator over the app window. Keep only
        // the real 20dp system inset here; the previous 40dp doubled it and
        // left the navigation items clipped at the top of a 110dp footer.
        paddingBottom: Platform.OS !== 'android' ? 0 : 20,
        gap: 0,
        height: Platform.OS !== 'android' ? 65 : 80,
        borderTopLeftRadius: 14,
        borderTopRightRadius: 14,
        borderTopWidth: 1,
        borderLeftWidth: 1,
        borderRightWidth: 1,
        borderColor: COLORS.softGrey,
    },
    tabItem: {
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        maxWidth: 72,
        height: 52,
        gap: 4,
    },
    tabIcon: {
        width: 24,
        height: 24,
        tintColor: COLORS.blueSteel,
    },
    tabIconActive: {
        tintColor: COLORS.steelNavy,
    },
    tabText: {
        width: '100%',
        fontSize: 13,
        lineHeight: 16,
        textAlign: 'center',
        includeFontPadding: false,
        color: COLORS.blueSteel,
    },
    tabTextActive: {
        color: COLORS.steelNavy,
        fontWeight: '500',
    },
    iconContainer: {
        position: 'relative',
    },
    badge: {
        position: 'absolute',
        top: -6,
        right: -10,
        minWidth: 18,
        height: 18,
        borderRadius: 9,
        backgroundColor: '#E53935',
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 4,
    },
    badgeText: {
        color: COLORS.white,
        fontSize: 10,
        fontWeight: '600',
    },
});

export default TabBar;

